// client/src/hooks/useTabKeyboard.js
import { useEffect } from 'react';

const NEW_TAB_PATH = '/';

/**
 * Browser-style tab shortcuts for the in-app strip.
 *
 *   Ctrl+T                 new tab
 *   Ctrl+Shift+T           reopen the last closed tab
 *   Ctrl+W                 close the active tab
 *   Ctrl+Tab / +Shift      next / previous tab
 *   Ctrl+1..8              jump to that tab; Ctrl+9 jumps to the last
 *
 * In an installed PWA some of these are browser-reserved and may never reach the
 * page; the strip's buttons and context menu stay the reliable path.
 */
export default function useTabKeyboard({
  tabs,
  activeId,
  openTab,
  closeTab,
  activateTab,
  reopenClosedTab,
}) {
  useEffect(() => {
    function onKeyDown(event) {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;

      const index = tabs.findIndex((t) => t.id === activeId);
      const key = event.key;

      if (key === 't' || key === 'T') {
        event.preventDefault();
        if (event.shiftKey) reopenClosedTab();
        else openTab(NEW_TAB_PATH);
        return;
      }
      if (key === 'w' || key === 'W') {
        // Only swallow the keystroke when we can actually honour it — closing
        // the last tab is a no-op, and eating Ctrl+W there would trap the user.
        if (tabs.length < 2) return;
        event.preventDefault();
        closeTab(activeId);
        return;
      }
      if (key === 'Tab' && tabs.length > 1) {
        event.preventDefault();
        const step = event.shiftKey ? -1 : 1;
        const next = (index + step + tabs.length) % tabs.length;
        activateTab(tabs[next].id);
        return;
      }
      if (!event.shiftKey && key >= '1' && key <= '9') {
        const target = key === '9' ? tabs.length - 1 : Number(key) - 1;
        if (target < 0 || target >= tabs.length) return;
        event.preventDefault();
        activateTab(tabs[target].id);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [tabs, activeId, openTab, closeTab, activateTab, reopenClosedTab]);
}
